import {Component, DestroyRef, EventEmitter, inject, Input, OnInit,
  Output, ViewChild, ElementRef, AfterViewInit, HostListener, OnDestroy} from '@angular/core';
import {ChatbotChat} from '../state/chatbot-chat';
import {CurrentUser} from '../../user/current-user.service';
import {ChatbotChatService} from '../api-services/chatbot-chat.service';
import {TranslatorService} from '../../translation/translator.service';
import {ChatbotMessagePair} from '../state/chatbot-message-pair';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ChatbotChatEventService} from '../utility-services/chatbot-chat-event.service';
import {Observable, BehaviorSubject} from 'rxjs';
import {take} from 'rxjs/operators';

@Component({
  selector: 'app-chatbot-chat-mobile-sidebar',
  templateUrl: './chatbot-chat-mobile-sidebar.component.html',
  styleUrls: ['./chatbot-chat-mobile-sidebar.component.scss']
})
export class ChatbotChatMobileSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
    selectedChat: ChatbotChat;

  @Output()
    chatSelected: EventEmitter<ChatbotChat> = new EventEmitter<ChatbotChat>();

  @Output()
    closeSidebar: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

  @ViewChild('shadowElement', { static: false })
    shadowElement!: ElementRef;

  public chatList$: Observable<ChatbotChat[]> = this.chatbotChatService.chats$;
  public loading$: Observable<boolean> = this.chatbotChatService.loading$;
  public pagination$ = this.chatbotChatService.pagination$;

  deleteFormOpen: boolean = false;
  currentChatList: ChatbotChat[] = [];

  private destroyRef = inject(DestroyRef);
  private boundScrollHandler = this.onMobileChatListScroll.bind(this);

  private componentLoading$ = new BehaviorSubject<boolean>(false);
  public readonly componentLoading = this.componentLoading$.asObservable();

  constructor(private currentUser: CurrentUser,
              public chatbotChatService: ChatbotChatService,
              private chatbotChatEventService: ChatbotChatEventService,
              private translatorService: TranslatorService) {
    this.closeSidebar.emit(false);
  }

  ngOnInit(): void {
    this.componentLoading$.next(true);
    this.chatbotChatService.resetChatList();
    this.chatbotChatService.loadInitialChats().subscribe({
      next: (chats) => {
        this.componentLoading$.next(false);
      },
      error: (error) => {
        this.componentLoading$.next(false);
      }
    });

    // Abonniere Chat-Liste für Auto-Load-Mechanismus (wie Desktop)
    this.chatList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(chats => {
      this.currentChatList = chats;

      // Nach Chat-Update prüfen ob Scrollbar vorhanden
      setTimeout(() => {
        this.updateScrollableClass();

        // Auto-Load wenn keine Scrollbar aber genug Chats
        this.checkAndTriggerAutoLoad('chatListUpdated');
      }, 100);
    });

    // Event-Listening für neue Nachrichten mit Component-spezifischer Behandlung
    this.chatbotChatEventService.messageSend$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Event-Deduplication: Verhindere mehrfache Verarbeitung
        if (!this.componentLoading$.value) {
          this.refreshChatList();
        }
      });
  }

  ngAfterViewInit(): void {
    // Setup Infinite Scroll für Mobile (wie Desktop)
    this.setupInfiniteScroll();
    this.updateScrollableClass();

    // Auto-Load-Check nach Initial-Setup
    setTimeout(() => {
      this.checkAndTriggerAutoLoad('ngAfterViewInit');
    }, 200);
  }

  private setupInfiniteScroll(): void {
    if (this.scrollContainer?.nativeElement) {
      // Finde den tatsächlich scrollbaren inneren Container (.chat-selections)
      const chatSelectionsElement = this.scrollContainer.nativeElement.querySelector('.chat-selections');

      if (chatSelectionsElement) {
        // Remove any existing listeners to avoid duplicates
        chatSelectionsElement.removeEventListener('scroll', this.boundScrollHandler);

        // Event-Listener am INNEREN Container anbringen
        chatSelectionsElement.addEventListener('scroll', (event: Event) => {
          this.onMobileChatListScroll(event);
          this.updateScrollableClass();
        }, { passive: true });
      }

      // Initial prüfen, ob scrollbar
      this.updateScrollableClass();

    }
    else {
      // Retry nach kurzer Verzögerung falls ViewChild noch nicht initialisiert
      setTimeout(() => {
        if (this.scrollContainer?.nativeElement) {
          this.setupInfiniteScroll();
        }
      }, 100);
    }
  }

  // Optimierte Scroll-Methode für Mobile-Chat-Container (wie Desktop)
  private onMobileChatListScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections');

    // Skip if loading more chats or no more chats to load
    this.chatbotChatService.pagination$.pipe(take(1)).subscribe(paginationState => {
      if (paginationState.loading || !paginationState.hasMore) {
        return;
      }

      // Prüfe, ob das Event vom richtigen Container kommt (.chat-selections)
      if (target !== chatSelectionsElement) {
        return;
      }

      const scrollElement = chatSelectionsElement as HTMLElement;
      if (!scrollElement) {
        return;
      }

      const threshold = 0.80; // Mobile-optimiert: Leicht niedriger als Desktop

      const scrollTop = scrollElement.scrollTop;
      const clientHeight = scrollElement.clientHeight;
      const scrollHeight = scrollElement.scrollHeight;

      const scrollableDistance = scrollHeight - clientHeight;
      const scrollPercentage = scrollableDistance > 0 ? (scrollTop / scrollableDistance) : 1;

      // Trigger Infinite Scroll mit Threshold am inneren Container
      if (scrollPercentage >= threshold) {
        this.loadMoreChats();
      }
    });
  }

  // Prüft, ob der Container scrollbar ist, und steuert festen Bottom-Schatten
  private updateScrollableClass(): void {
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections') as HTMLElement;
    const shadowElement = this.shadowElement?.nativeElement as HTMLElement;

    if (!chatSelectionsElement) {
      return;
    }

    const isScrollable = chatSelectionsElement.scrollHeight > chatSelectionsElement.clientHeight;

    // Steuere Top-Schatten im scrollenden Container
    if (isScrollable) {
      chatSelectionsElement.classList.add('is-scrollable');
    }
    else {
      chatSelectionsElement.classList.remove('is-scrollable');
    }

    // FESTER BOTTOM-SCHATTEN: Außerhalb des scrollenden Bereichs
    if (shadowElement) {
      if (isScrollable) {
        shadowElement.classList.add('visible');
      }
      else {
        shadowElement.classList.remove('visible');
      }
    }
  }

  public loadMoreChats(): void {
    this.chatbotChatService.pagination$.pipe(take(1)).subscribe(paginationState => {
      if (!paginationState.hasMore) {
        return;
      }

      if (paginationState.loading) {
        return;
      }

      this.chatbotChatService.loadNextPage().subscribe({
        next: (newChats) => {
          if (newChats.length === 0) {
            this.chatbotChatService.updatePaginationState({hasMore: false});
          }
        },
        error: (error) => {
          console.error('LoadNextPage failed:', error);
          this.chatbotChatService.updatePaginationState({loading: false, error});
        }
      });
    });
  }

  public refreshChatList(): void {
    // Component-spezifisches Loading-State
    this.componentLoading$.next(true);

    // Verwende neue debounced refreshChatList-Methode
    this.chatbotChatService.refreshChatList().subscribe({
      next: () => {
        this.componentLoading$.next(false);
      },
      error: (error) => {
        console.error('Error refreshing chat list:', error);
        this.componentLoading$.next(false);
      }
    });
  }

  newChat(): void {
    this.selectedChat = null;
    this.chatSelected.emit(this.selectedChat);
  }

  sidebarEnable(): void {
    this.closeSidebar.emit(true);
  }

  selectChat(chat: ChatbotChat): void {
    this.selectedChat = chat;
    this.chatSelected.emit(chat);
  }

  addChatToChatList(chat: ChatbotChat): void {
    // Bei neuem Chat einfach die Liste refreshen
    this.refreshChatList();
    this.selectChat(chat);
  }

  deleteChatFromChatList(chat: ChatbotChat): void {
    this.chatbotChatService.deleteChatByID(chat.chat_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (chat && this.selectedChat && chat.chat_id === this.selectedChat.chat_id) {
        this.newChat();
      }
      this.refreshChatList();
    });
  }

  public trackByChatId(index: number, chat: ChatbotChat): number {
    return chat.chat_id;
  }

  deleteAllChats(): void {
    this.chatSelected.emit(null);
    this.chatbotChatService.deleteAllChats().subscribe(value => {
      console.log('deleteAllChats at chatbot-chat-selections', value);
      this.refreshChatList();
    });
    this.deleteFormOpen = false;
  }

  openDeleteForm(): void {
    this.deleteFormOpen = true;
  }

  closeDeleteForm(): void {
    this.deleteFormOpen = false;
  }

  // Component-spezifisches Event-Handling mit HostListener
  @HostListener('scroll', ['$event'])
  onContainerScroll(event: Event): void {
    // Scope-Isolation: Nur auf spezifische Container reagieren
    const target = event.target as HTMLElement;

    if (!target.closest('.chat-sidebar') || target.closest('.chat-messages-container')) {
      return;
    }

    // Event-Deduplication: Verhindere mehrfache Verarbeitung
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    // Cleanup von Event-Listenern am inneren Container
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections');
    if (chatSelectionsElement) {
      chatSelectionsElement.removeEventListener('scroll', this.boundScrollHandler);
    }

    this.componentLoading$.complete();
  }

  // Auto-Load-Mechanismus für Mobile-Lazy-Loading (wie Desktop)
  private checkAndTriggerAutoLoad(context: string): void {
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections') as HTMLElement;

    if (!chatSelectionsElement) {
      return;
    }

    const isScrollable = chatSelectionsElement.scrollHeight > chatSelectionsElement.clientHeight;
    const hasEnoughChats = this.currentChatList.length >= 6;

    // Auto-Load triggern wenn:
    // 1. Keine Scrollbar vorhanden
    // 2. Mindestens 6 Chats geladen
    // 3. Pagination hat noch mehr Chats
    // 4. Nicht bereits am Laden

    this.chatbotChatService.pagination$.pipe(take(1)).subscribe(paginationState => {
      const shouldAutoLoad = !isScrollable &&
                            hasEnoughChats &&
                            paginationState.hasMore &&
                            !paginationState.loading &&
                            this.currentChatList.length < 50; // Sicherheits-Limit

      if (shouldAutoLoad) {
        this.loadMoreChats();

        // Rekursiver Check nach dem Laden
        setTimeout(() => {
          this.checkAndTriggerAutoLoad(`${context}-retry`);
        }, 500);
      }
    });
  }

  __(key: string): string {
    return this.translatorService.translate('chatbot-chat-selections.' + key);
  }
}
